import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, lazy, Suspense } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { WhatsAppFloatingButton } from './components/layout/WhatsAppFloatingButton';
import { TenantProvider, useTenantContext } from './context/TenantContext';
import { dataLoader } from './services/dataLoader.service';

// Páginas cargadas de forma diferida para code splitting por ruta
const HomePage = lazy(() => import('./pages/DynamicHomePage'));
const VentasPage = lazy(() => import('./pages/VentasPage'));
const ArriendosPage = lazy(() => import('./pages/ArriendosPage'));
const ServiciosPage = lazy(() => import('./pages/ServiciosPage'));
const QuienesSomosPage = lazy(() => import('./pages/QuienesSomosPage'));
const ContactoPage = lazy(() => import('./pages/ContactoPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const PropertyDetailPage = lazy(() => import('./pages/PropertyDetailPage'));
const VenderPage = lazy(() => import('./pages/VenderPage'));
const TasacionPage = lazy(() => import('./pages/TasacionPage'));

// Loading Component
function LoadingScreen() {
 return (
 <div className="min-h-screen flex items-center justify-center bg-gray-50">
 <div className="text-center">
 <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
 <p className="text-gray-600">Cargando...</p>
 </div>
 </div>
 );
}

// Error Component
// Redirige /arriendos -> /alquiler conservando query string (necesario para
// que el override `?tenant=<slug>` no se pierda en dev/preview).
function ArriendosRedirect() {
 const { search } = useLocation();
 return <Navigate to={`/alquiler${search}`} replace />;
}

function ErrorScreen({ error, onRetry }: { error: string; onRetry: () => void }) {
 return (
 <div className="min-h-screen flex items-center justify-center bg-gray-50">
 <div className="text-center max-w-md px-4">
 <h2 className="text-xl font-semibold mb-2 text-gray-900">No se pudo cargar la configuración</h2>
 <p className="text-gray-600 mb-4">{error}</p>
 <button
 onClick={onRetry}
 className="px-4 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors"
 >
 Reintentar
 </button>
 </div>
 </div>
 );
}

function AppInner() {
 const { config, loading, error, refetch } = useTenantContext();
 const [dataLoading, setDataLoading] = useState(false);
 const [appReady, setAppReady] = useState(false);

 // Update document metadata when config changes
 useEffect(() => {
 if (config) {
 // Update document title
 document.title = config.seo.title;
 
 // Update meta description
 let metaDescription = document.querySelector('meta[name="description"]');
 if (!metaDescription) {
 metaDescription = document.createElement('meta');
 metaDescription.setAttribute('name', 'description');
 document.head.appendChild(metaDescription);
 }
 metaDescription.setAttribute('content', config.seo.description);

 // Update favicon
 let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
 if (!favicon) {
 favicon = document.createElement('link');
 favicon.setAttribute('rel', 'icon');
 document.head.appendChild(favicon);
 }
 favicon.href = config.favicon;

 // Update meta keywords
 let metaKeywords = document.querySelector('meta[name="keywords"]');
 if (!metaKeywords) {
 metaKeywords = document.createElement('meta');
 metaKeywords.setAttribute('name', 'keywords');
 document.head.appendChild(metaKeywords);
 }
 metaKeywords.setAttribute('content', config.seo.keywords.join(', '));

 // Update OG tags
 let ogTitle = document.querySelector('meta[property="og:title"]');
 if (!ogTitle) {
 ogTitle = document.createElement('meta');
 ogTitle.setAttribute('property', 'og:title');
 document.head.appendChild(ogTitle);
 }
 ogTitle.setAttribute('content', config.seo.title);

 let ogDescription = document.querySelector('meta[property="og:description"]');
 if (!ogDescription) {
 ogDescription = document.createElement('meta');
 ogDescription.setAttribute('property', 'og:description');
 document.head.appendChild(ogDescription);
 }
 ogDescription.setAttribute('content', config.seo.description);

 let ogImage = document.querySelector('meta[property="og:image"]');
 if (!ogImage) {
 ogImage = document.createElement('meta');
 ogImage.setAttribute('property', 'og:image');
 document.head.appendChild(ogImage);
 }
 ogImage.setAttribute('content', config.seo.ogImage);
 }
 }, [config]);

 // Preload data when config is available
 useEffect(() => {
 if (config && !appReady) {
 setDataLoading(true);
 
 async function preloadData() {
 try {
 await dataLoader.preloadData(['/']);
 } catch (error) {
 console.error('Error preloading data:', error);
 // Don't fail the app if preloading fails
 } finally {
 setDataLoading(false);
 setAppReady(true);
 }
 }
 
 preloadData();
 }
 }, [config, appReady]);

 // Show loading screen while tenant is loading or data is preloading
 if (loading || (config && dataLoading)) {
 return <LoadingScreen />;
 }

 // Show error screen if we couldn't load config and it's a critical error
 if (!config && error && !error.includes('cached') && !error.includes('default')) {
 return <ErrorScreen error={error} onRetry={refetch} />;
 }

 // If we still don't have config, something is really wrong
 if (!config) {
 return (
 <ErrorScreen 
 error="Unable to load application configuration" 
 onRetry={refetch} 
 />
 );
 }

 return (
 <HelmetProvider>
 <div 
 className="min-h-screen flex flex-col"
 style={{
 '--primary-color': config.primaryColor,
 '--secondary-color': config.secondaryColor,
 '--accent-color': config.accentColor,
 } as React.CSSProperties}
 >
 {/* Error banner for non-critical errors */}
 {error && (
 <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
 <div className="flex items-center justify-between text-yellow-800 text-sm">
 <span>{error}</span>
 <button
 onClick={refetch}
 className="text-yellow-600 hover:text-yellow-800 underline"
 >
 Retry
 </button>
 </div>
 </div>
 )}

 <Header />
 <main className="flex-1">
 {/* Suspense boundary para las páginas cargadas con lazy — muestra el spinner de carga mientras se descarga el chunk */}
 <Suspense fallback={<LoadingScreen />}>
 <Routes>
 <Route path="/" element={<HomePage />} />
 <Route path="/ventas" element={<VentasPage />} />
 <Route path="/alquiler" element={<ArriendosPage />} />
 <Route path="/arriendos" element={<ArriendosRedirect />} />
 <Route path="/servicios" element={<ServiciosPage />} />
 <Route path="/quienes-somos" element={<QuienesSomosPage />} />
 <Route path="/contacto" element={<ContactoPage />} />
 <Route path="/vender" element={<VenderPage />} />
 <Route path="/tasacion" element={<TasacionPage />} />
 {config.features.blog && (
 <>
 <Route path="/blog" element={<BlogPage />} />
 <Route path="/blog/:slug" element={<BlogPostPage />} />
 </>
 )}
 <Route path="/propiedad/:id" element={<PropertyDetailPage />} />
 </Routes>
 </Suspense>
 </main>
 <Footer />
 <WhatsAppFloatingButton />
 </div>
 </HelmetProvider>
 );
}

function App() {
 return (
 <TenantProvider>
 <AppInner />
 </TenantProvider>
 );
}

export default App;